import { logger } from '@/lib/logger'
import { logError } from '@gforce/shared-errors'
import { safeErrorDigest } from '@/lib/errors/safe-error-kind'

/**
 * リアルタイム購読（自動更新の常時接続）の開始を、失敗しても画面を落とさない形で行う。
 *
 * なぜ必要か（2026-08-09 実測）:
 *   常時接続（WebSocket）を開けないとき、購読処理は**同期例外**を投げる。
 *   購読はどの画面でも useEffect の中で行われるが、**useEffect 内の throw も
 *   ErrorBoundary まで伝播する**ため、認証済みアプリ全体が
 *   「エラーが発生しました」に差し替わり、案件一覧も検索も使えなくなる。
 *
 *   しかもこの落ち方は iPad(Safari/WebKit) 限定で出る。WebKit は WebSocket の生成が
 *   拒否されると同期例外を投げるのに対し、Chromium は非同期の error イベントにするため
 *   落ちない。「iPad の人だけ真っ白、PC の人は平気」という、原因の分かりにくい形で出る
 *   （2026-08-08 の接続枯渇障害と同じ見え方）。
 *
 * 方針:
 *   自動更新は「あると便利」な機能であって、これが使えないことは業務を止める理由にならない。
 *   失うのは自動更新だけに閉じ、画面は通常どおり使えるようにする。
 *   ただし黙って捨てない（自動更新が効いていないことを追跡できるようにする）。
 *
 * SSOT:
 *   同じ受け止めを各画面へコピーしない。realtime 購読を張る箇所はこの関数を通す。
 *   回帰ガード: lib/realtime/__tests__/safe-realtime.test.ts
 *
 * 既知の限界（Codex SO 2026-08-09・LOW）:
 *   ここで受け止めるのは**同期例外**のみ。購読後に非同期で来る CHANNEL_ERROR / timeout は対象外
 *   （今回の差分が悪化させたものではなく、既存仕様のまま）。
 *   また、自動更新が止まったことは現状ログにしか出ない。realtime が「あると便利」ではなく
 *   業務上の整合性維持に必須な画面へ広げるときは、戻り値を UI 表示か監視へ接続すること。
 *
 * @returns 購読を開始できたら true、失敗したら false
 *   （現時点の呼び出し元はいずれも戻り値を使っていない。上記の縮退表示を足すときの接続点）
 */
export function safeRealtimeSubscribe(label: string, subscribe: () => void): boolean {
  try {
    subscribe()
    return true
  } catch (error) {
    // ログだけだと「iPad の人が自動更新を失っている」ことに誰も気づけない。監視へ送る。
    logError(error, `realtime:subscribe:${label}`)
    logger.warn(`リアルタイム購読を開始できませんでした (${label})。自動更新なしで続行します`, {
      errorSummary: safeErrorDigest(error),
    })
    return false
  }
}

/**
 * 購読の後片付け（解除）を、失敗しても unmount を壊さない形で行う。
 * 解除の失敗で二次被害（別画面へ遷移できない等）を出さないための対。
 *
 * @returns 解除できたら true、失敗したら false。
 *   呼び出し元がチャンネル参照を保持している場合、**false のときは参照を捨ててはならない**。
 *   捨てると Supabase 側にチャンネルが残ったまま再解除できなくなり、リークを隠す
 *   （Codex SO MEDIUM・2026-08-09）。
 */
export function safeRealtimeCleanup(label: string, cleanup: () => void): boolean {
  try {
    cleanup()
    return true
  } catch (error) {
    logError(error, `realtime:cleanup:${label}`)
    logger.warn(`リアルタイム購読の解除に失敗しました (${label})`, {
      errorSummary: safeErrorDigest(error),
    })
    return false
  }
}
